import { Button } from "@/components/ui/button";
import type { ContactMessage } from "../../types/contact.types";
import { formatActivityTimestamp } from "@/lib/dateUtils";

interface RequestDetailPanelProps {
  message: ContactMessage;
  onBack: () => void;
  onMarkAsUnread: () => void;
}

export function RequestDetailPanel({
  message,
  onBack,
  onMarkAsUnread,
}: RequestDetailPanelProps) {
  return (
    <div className="bg-black-950 flex h-full w-full flex-col gap-6 overflow-hidden rounded-lg">
      {/* Header - Fixed at top */}
      <div className="flex flex-shrink-0 flex-wrap items-center gap-3 px-3 py-3 sm:gap-6 sm:px-5">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="hover:bg-dark-surface gap-1 p-1.5 text-xs font-bold text-white hover:text-white/90"
        >
          <i className="ri-arrow-left-long-line text-md font-normal"></i>
          Back
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={onMarkAsUnread}
          className="hover:bg-dark-surface gap-1 p-1.5 text-xs font-bold text-white hover:text-white/90"
        >
          <i className="ri-mail-unread-line text-md font-normal"></i>
          <span className="hidden sm:inline">Mark as unread</span>
          <span className="sm:hidden">Unread</span>
        </Button>
      </div>

      {/* Content - Scrollable */}
      <div className="scrollbar-thin flex flex-1 flex-col gap-3 overflow-y-auto px-3 pb-5 sm:px-5">
        <div className="flex-1 space-y-6">
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-between">
            {/* Title */}
            <div className="">
              <h2 className="text-lg leading-6 font-bold -tracking-wide text-white">
                {message.name}
              </h2>
              <p className="text-black-400 text-md leading-6 font-bold -tracking-wide">
                {message.company}
              </p>
            </div>

            {/* Date */}
            <div className="sm:text-right">
              <p className="text-black-500 sm:text-md text-sm leading-6 -tracking-wide">
                {formatActivityTimestamp(message.created_at)}
              </p>
            </div>
          </div>

          {/* Message */}
          <p className="sm:text-md text-sm leading-6 font-medium tracking-normal whitespace-pre-wrap text-white sm:leading-7">
            {message.message}
          </p>
        </div>

        {/* Contact Info */}
        <div className="bg-dark-surface mt-4 space-y-3 rounded-lg p-3 transition-all duration-200 hover:scale-105 hover:bg-current/5 sm:mt-6 sm:p-4">
          <a href={`mailto:${message.email}`}>
            <p className="text-xs font-medium text-gray-500">Email</p>
            <p className="text-sm break-all text-gray-300">{message.email}</p>
          </a>
        </div>
      </div>
    </div>
  );
}
